import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import type { MessageKey } from "./i18n";
import { NavLink, Link, Outlet, Route, Routes, useLocation, useNavigationType } from "react-router";
import { t } from "./i18n";
import { getMe, type Me } from "./api";
import type { SessionCtx } from "./session";
import { HomePage } from "./pages/HomePage";
import { BrandMark } from "./pages/BrandMark";
import { InstallPage } from "./pages/InstallPage";
import { SignInPage } from "./pages/SignInPage";
import { UserMenu } from "./pages/UserMenu";
import { PricingNavLink } from "./pages/PricingNavLink";
import { GuidePage } from "./pages/GuidePage";
import { SupportPage } from "./pages/SupportPage";
import { LandingPage } from "./pages/LandingPage";
import { PricingPage } from "./pages/PricingPage";
import { SubscribePage } from "./pages/SubscribePage";
import { ShieldCheckIcon } from "./pages/icons";
import { LANDING_PAGES, publicRoute } from "./landing";
import { canonicalUrl } from "./seo";
import { PRICING_PUBLIC } from "./flags";
import { SELLER } from "./seller";

// Pages behind sign-in load on demand: the public pages' bundle stays small (BL-156). Public pages stay
// in the main chunk - they are pre-rendered and hydrated (ADR-0050).
const ConnectionsPage = lazy(() => import("./pages/ConnectionsPage").then((m) => ({ default: m.ConnectionsPage })));
const ClientsMorePage = lazy(() => import("./pages/ClientsMorePage").then((m) => ({ default: m.ClientsMorePage })));
const SdkPage = lazy(() => import("./pages/SdkPage").then((m) => ({ default: m.SdkPage })));
const PreviewPage = lazy(() => import("./pages/PreviewPage").then((m) => ({ default: m.PreviewPage })));
const ConsentPage = lazy(() => import("./pages/ConsentPage").then((m) => ({ default: m.ConsentPage })));
const AccountPage = lazy(() => import("./pages/AccountPage").then((m) => ({ default: m.AccountPage })));
const AdminFeedbackPage = lazy(() => import("./pages/AdminFeedbackPage").then((m) => ({ default: m.AdminFeedbackPage })));
const AdminBillingPage = lazy(() => import("./pages/AdminBillingPage").then((m) => ({ default: m.AdminBillingPage })));
const later = (page: ReactNode) => <Suspense fallback={null}>{page}</Suspense>;


// Footer «Sources» row: the landing pages (ADR-0036), short labels from en.ts.
const FOOT_SOURCES: [string, MessageKey][] = [
  ["/mcp/app-store-connect", "footer.src.asc"],
  ["/mcp/appsflyer", "footer.src.appsflyer"],
  ["/mcp/revenuecat", "footer.src.revenuecat"],
  ["/mobile-app-marketing-mcp", "footer.src.mobile"],
  ["/mcp/google-analytics-4", "footer.src.ga4"],
  ["/mcp/search-console", "footer.src.searchConsole"],
  ["/mcp/google-ads", "footer.src.googleAds"],
  ["/mcp/meta-ads", "footer.src.metaAds"],
  ["/mcp/tiktok-ads", "footer.src.tiktok"],
  ["/mcp/apple-ads", "footer.src.appleAds"],
  ["/mcp/google-play", "footer.src.googlePlay"],
  ["/mcp/instagram", "footer.src.instagram"],
  ["/mcp/facebook-pages", "footer.src.fbPages"],
];

function Layout() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    getMe()
      .then((m) => alive && setMe(m))
      .catch(() => alive && setMe(null));
    return () => {
      alive = false;
    };
  }, []);

  const { pathname } = useLocation();
  const home = pathname === "/" || pathname === "/guide" || pathname === "/support" || pathname === "/pricing" || LANDING_PAGES.some((p) => p.path === pathname);
  // The consent screen is a standalone page: logo only, a minimal footer.
  // Under 720px the header is the logo and the avatar (Mobile mockup); the links move into the user menu.
  const consent = pathname === "/oauth/consent";

  // A new page opens at the top (owner 2026-10-04: landing pages opened mid-page after a link from the
  // home page). Links with an anchor (#appsflyer, #plugin) keep scrolling to it; reload and Back keep the
  // browser's own position.
  const { hash } = useLocation();
  const navType = useNavigationType();
  const firstLoad = useRef(true);
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    if (navType === "POP") return;
    if (hash && document.getElementById(decodeURIComponent(hash.slice(1)))) return;
    window.scrollTo(0, 0);
    // Only a change of page: a #guide anchor on the same page (the source modals) must not jump to the top.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // One title per page for search results and tabs (ADR-0033). Public pages also carry their description,
  // canonical and Open Graph (pre-rendered per page, ADR-0036): keep them in step on client navigation.
  useEffect(() => {
    const titles: Record<string, MessageKey> = {
      "/install": "meta.title.install",
      "/guide": "meta.title.guide",
      "/support": "meta.title.support",
      "/connections": "meta.title.connections",
      "/clients/more": "meta.title.clientsMore",
      "/sdk": "meta.title.sdk",
      "/preview": "meta.title.preview",
      "/account": "meta.title.account",
      "/admin/feedback": "meta.title.adminFeedback",
      "/admin/billing": "meta.title.adminBilling",
      "/oauth/consent": "meta.title.consent",
      "/signin": "meta.title.signin",
      "/subscribe": "subscribe.metaTitle",
    };
    const route = publicRoute(pathname);
    document.title = route?.title ?? t(titles[pathname] ?? "meta.title");
    if (!route) return;
    const set = (sel: string, attr: string, value: string) => document.head.querySelector(sel)?.setAttribute(attr, value);
    const url = canonicalUrl(route.path);
    set('meta[name="description"]', "content", route.description);
    set('link[rel="canonical"]', "href", url);
    set('meta[property="og:url"]', "content", url);
    set('meta[property="og:title"]', "content", route.title);
    set('meta[property="og:description"]', "content", route.description);
  }, [pathname]);

  return (
    <div className={`page${consent ? " page-consent" : ""}`}>
      <div className="site-bar">
      <header className="nav site-nav shell">
        <Link to="/" className="nav-brand">
          <BrandMark size={32} />
          <span className="brand-lockup">
            <span>{t("brand")}</span>
            <span className="brand-second">{t("brand.second")}</span>
          </span>
        </Link>
        {me && !consent && (
          <nav className="nav-links">
            <NavLink to="/connections" className="tone-green">{t("nav.connections")}</NavLink>
            <NavLink to="/preview" className="tone-sky">{t("nav.preview")}</NavLink>
            <NavLink to="/install" className="tone-tangerine">{t("nav.install")}</NavLink>
            <PricingNavLink />
          </nav>
        )}
        {/* Signed out: «Install in Claude or ChatGPT» («Install» on a phone) and «Pricing» after the logo (owner 2026-11-02;
            Home and Pricing mockups), the price only while it is public (ADR-0048). `!me`, not `=== null`: the pre-rendered public
            pages keep the signed-out first paint (BL-140, ADR-0050); pages served by app.html show no links until the session is
            known (BL-146). */}
        {(me === null || (me === undefined && publicRoute(pathname))) && !consent && (
          <nav className="nav-links nav-links-public">
            <NavLink to="/install" className="tone-tangerine">
              <span className="nav-public-long">{t("nav.installPublic")}</span>
              <span className="nav-public-short">{t("nav.install")}</span>
            </NavLink>
            {PRICING_PUBLIC && <NavLink to="/pricing" className="tone-sky">{t("nav.pricing")}</NavLink>}
          </nav>
        )}
        <div className="nav-end">
          {me && !consent && <UserMenu me={me} />}
          {/* Unknown session paints as signed out with the place kept, so nothing moves for a guest (ADR-0050). */}
          {!me && !consent && (
            <Link
              to="/signin"
              className={`btn btn-primary nav-signin${me === undefined ? " pending" : ""}`}
              inert={me === undefined}
              aria-hidden={me === undefined || undefined}
            >
              {t("nav.signin")}
            </Link>
          )}
        </div>
      </header>
      </div>
      <main className={`shell ${home ? "home" : consent ? "main-card main-consent" : "main-card"}`}>
        <Outlet context={{ me } satisfies SessionCtx} />
      </main>
      <footer className="site-foot shell">
        {!consent && (
          <p className="foot-about" data-testid="foot-about">
            {t("brand")} · {t("footer.about")}
          </p>
        )}
        <div className="foot-inner">
          {!consent && (
            <span className="foot-trust">
              <ShieldCheckIcon size={16} />
              <span>{t("footer.trust")}</span>
            </span>
          )}
          <span className="foot-links">
            <a href="https://valas.team/" target="_blank" rel="noopener">{t("footer.copy", { name: SELLER.tradeName, city: SELLER.city, country: SELLER.country })}</a>
            {!consent && <Link to="/guide">{t("footer.guide")}</Link>}
            {!consent && <Link to="/support">{t("footer.support")}</Link>}
            {!consent && <Link to="/vs">{t("footer.vs")}</Link>}
            {!consent && <Link to="/for-agencies">{t("footer.agencies")}</Link>}
            {!consent && <Link to="/mcp/any-app">{t("footer.anyApp")}</Link>}
            <a href="/privacy.html">{t("footer.privacy")}</a>
            <a href="/terms.html">{t("footer.terms")}</a>
            {PRICING_PUBLIC && <a href="/refunds.html">{t("footer.refunds")}</a>}
            {!consent && <a href="/datadeletion">{t("footer.deletion")}</a>}
            <a href={`mailto:${SELLER.email}`}>{SELLER.email}</a>
          </span>
        </div>
        {!consent && (
          <nav className="foot-sources" aria-label={t("footer.sources")}>
            <span className="foot-sources-label">{t("footer.sources")}</span>
            {FOOT_SOURCES.filter(([path]) => LANDING_PAGES.some((p) => p.path === path)).map(([path, key]) => (
              <Link key={path} to={path}>
                {t(key)}
              </Link>
            ))}
          </nav>
        )}
      </footer>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="connections" element={later(<ConnectionsPage />)} />
        <Route path="clients/more" element={later(<ClientsMorePage />)} />
        <Route path="sdk" element={later(<SdkPage />)} />
        <Route path="preview" element={later(<PreviewPage />)} />
        <Route path="install" element={<InstallPage />} />
        <Route path="guide" element={<GuidePage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="subscribe" element={<SubscribePage />} />
        {LANDING_PAGES.map((p) => (
          <Route key={p.path} path={p.path.slice(1)} element={<LandingPage page={p} />} />
        ))}
        <Route path="oauth/consent" element={later(<ConsentPage />)} />
        <Route path="signin" element={<SignInPage />} />
        <Route path="account" element={later(<AccountPage />)} />
        <Route path="admin/feedback" element={later(<AdminFeedbackPage />)} />
        <Route path="admin/billing" element={later(<AdminBillingPage />)} />
        <Route path="*" element={<HomePage />} />
      </Route>
    </Routes>
  );
}
